/**
 * RNR's Tabs, wearing the design system's pill track.
 *
 * The registry component is shadcn's grey-slab list; the design file draws a pill on --card with the
 * active trigger tinted --accent, and that is the version the whole fleet is meant to recognise.
 * Labels are never truncated and never shrunk — a label squeezed to fit is a target too small to
 * hit — so a track that overflows is put in a horizontal ScrollView by its caller.
 */
import { TextClassContext } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import * as TabsPrimitive from '@rn-primitives/tabs';
import { Platform } from 'react-native';

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root className={cn('flex flex-col gap-2', className)} {...props} />;
}

function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn(
        'bg-card border-border flex flex-row items-center gap-1 self-start rounded-full border p-1',
        Platform.select({ web: 'w-fit' }),
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const { value } = TabsPrimitive.useRootContext();
  const active = props.value === value;
  return (
    <TextClassContext.Provider value={cn('text-small', active ? 'text-accent-foreground font-semibold' : 'text-muted-foreground')}>
      <TabsPrimitive.Trigger
        className={cn(
          'flex flex-row items-center justify-center gap-1.5 rounded-full px-4 py-2',
          Platform.select({
            web: 'focus-visible:border-ring focus-visible:ring-ring/50 cursor-default whitespace-nowrap transition-colors focus-visible:ring-[3px] disabled:pointer-events-none',
          }),
          props.disabled && 'opacity-50',
          /**
           * THE ACTIVE PILL CARRIES A HAIRLINE AS WELL AS ITS FILL.
           *
           * --accent sits about seven points of lightness off --card in the light themes, which is
           * plenty, and about the same off a --card that is already at 15% in the dark ones, which
           * is not: on a dark capture the chosen tab was carried by its label's colour alone and
           * the track read as one unbroken pill. The border is on EVERY trigger and transparent on
           * the inactive ones, so choosing a tab changes a colour and never a size — a 1px edge
           * that appears only when active moves the label a pixel every time you switch.
           */
          'border border-transparent',
          active && 'bg-accent border-line-strong',
          className,
        )}
        {...props}
      />
    </TextClassContext.Provider>
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content className={cn(Platform.select({ web: 'flex-1 outline-none' }), className)} {...props} />;
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
